/**
 * UserManagement Container Component
 * Manages user data fetching, authentication, and state for tenant user management.
 * Delegates presentation to UserList component following container/presentational pattern.
 */

import { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { makeStyles, Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Text, Spinner, shorthands, tokens } from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, ArrowUpload20Regular, Certificate20Regular, ArrowClockwise20Regular } from '@fluentui/react-icons';
import { UserApiService } from '../services/userApi';
import { OrganizationApiService } from '../services/organizationApi';
import { useOrganizationUsers } from '../hooks/useOrganizationUsers';
import { useOrganizationTenants } from '../hooks/useOrganizationTenants';
import { updateUserRole } from '../services/userAdminApi';
import { isGlobalAdmin, type UserRole } from '../types/auth';
import { UserList } from '../components/UserManagement/UserList';
import { UserModal, PasswordResetDialog, BulkImportModal, BulkLicenseDialog, CSVPreviewModal } from '../components/UserManagement/dialogs';
import { type ParsedCSVData, CSVResultsModal } from '../components/UserManagement/csv';
import { PasswordlessStatusIndicator } from '../components/UserManagement/PasswordlessStatusIndicator';
import { TenantContextSelector } from '../components/TenantManagement/TenantContextSelector';
import { useTenantContext } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PageLayout,
  DataGridToolbar,
  ToolbarButton,
  ToolbarSearch,
} from '../components/layouts';
import { RoundedCard } from '../components/common/RoundedCard';
import { Alert } from '../components/common/Alert';
import type { TenantUser, UserCreateRequest, UserUpdateRequest, BulkUserResult } from '../types/user';

/**
 * Props for UserManagementPage component.
 */
interface UserManagementPageProps {
  /**
   * Optional organization ID to filter users by.
   * When provided, only users in this organization are shown.
   */
  organizationId?: number;
  /**
   * Optional tenant ID to use for user management.
   * When provided, overrides the tenant from TenantContext.
   * Used when embedding in WorkspaceDetailPage or OrganizationDetailPage.
   */
  tenantId?: string;
  /**
   * When true, hides the header card (title, tabs, search, buttons).
   * Used when embedding this component inside another page that provides its own header.
   */
  embedded?: boolean;
  /**
   * Controlled search query value.
   * When provided, the component uses this value instead of internal state.
   */
  searchQuery?: string;
  /**
   * Callback fired when search query changes.
   * Used for controlled mode or to sync search state with parent.
   */
  onSearchChange?: (query: string) => void;
  /**
   * Callback fired when user selection changes.
   * Reports the count of selected users for parent to update button states.
   */
  onSelectionChange?: (count: number) => void;
  /**
   * Optional column view override.
   * When provided, uses this view instead of the default logic.
   */
  columnView?: 'full' | 'auth-only' | 'organization' | 'workspace';
}

/**
 * Ref handle for UserManagementPage.
 * Exposes methods that parent components can call when embedding.
 */
export interface UserManagementPageRef {
  /** Refresh the user list (syncs with Azure AD) */
  refresh: () => void;
  /** Open the bulk import modal */
  openBulkImport: () => void;
  /** Open the add user modal */
  openAddUser: () => void;
  /** Open bulk license dialog for selected users */
  openBulkLicense: () => void;
  /** Open bulk delete confirmation for selected users */
  openBulkDelete: () => void;
}

const useStyles = makeStyles({
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  paginationInfo: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  pageText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
});

export const UserManagementPage = forwardRef<UserManagementPageRef, UserManagementPageProps>(function UserManagementPage({
  organizationId,
  tenantId: tenantIdProp,
  embedded,
  searchQuery: controlledSearchQuery,
  onSearchChange,
  onSelectionChange,
  columnView: columnViewProp,
}, ref) {
  const styles = useStyles();
  const { currentTenant } = useTenantContext();
  const { user: currentUser } = useAuth();
  // Use prop if provided, otherwise fall back to TenantContext.
  // When organizationId is provided without tenantIdProp, pass undefined to let the
  // backend auto-resolve the tenant_id based on the organization's authentication mode:
  // - home_tenant: uses settings.azure_tenant_id (home tenant)
  // - federated: uses the organization's azure_tenant_id
  const tenantId = tenantIdProp ?? (organizationId ? undefined : currentTenant?.tenant_id);

  // Pagination state (controls what the hooks fetch)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 20;

  // React Query hooks for data fetching
  const {
    users,
    total: totalUsers,
    isLoading: loading,
    isSyncing,
    error: usersError,
    refetch: refetchUsers,
    syncUsers,
  } = useOrganizationUsers({
    organizationId,
    page: currentPage,
    pageSize,
    tenantId,
  });

  const {
    tenants: organizationTenants,
  } = useOrganizationTenants(organizationId);

  // Available organizations for transfer (global_admin only, home_tenant auth mode)
  const [availableOrganizations, setAvailableOrganizations] = useState<Array<{ id: number; name: string }>>([]);
  const organizationsFetchedRef = useRef(false);

  // Fetch organizations for transfer dropdown (global_admin only)
  useEffect(() => {
    if (!isGlobalAdmin(currentUser) || organizationsFetchedRef.current) {
      return;
    }

    const fetchOrganizations = async () => {
      try {
        organizationsFetchedRef.current = true;
        const response = await OrganizationApiService.listOrganizations({ page_size: 100 });
        // Filter to only home_tenant auth mode organizations
        const homeTenantOrgs = response.items
          .filter(org => org.authentication_mode === 'home_tenant')
          .map(org => ({ id: org.id, name: org.name }));
        setAvailableOrganizations(homeTenantOrgs);
      } catch {
        // Silently fail - organization transfer will just not be available
      }
    };

    fetchOrganizations();
  }, [currentUser]);

  // UI state for errors and success messages (from mutations)
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Derive error from hook or mutations
  const error = usersError?.message ?? mutationError;

  // Support controlled/uncontrolled search state
  const [internalSearchQuery, setInternalSearchQuery] = useState<string>('');
  const searchQuery = controlledSearchQuery ?? internalSearchQuery;

  const handleSearchInputChange = (value: string) => {
    // Call callback if provided
    if (onSearchChange) {
      onSearchChange(value);
    }
    // Update internal state if not controlled
    if (controlledSearchQuery === undefined) {
      setInternalSearchQuery(value);
    }
  };

  // Tab state with localStorage persistence
  const [selectedTab, setSelectedTab] = useState<'users' | 'auth'>(() => {
    const stored = localStorage.getItem('userManagementSelectedTab');
    return (stored === 'auth' ? 'auth' : 'users') as 'users' | 'auth';
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedUser, setSelectedUser] = useState<TenantUser | undefined>(undefined);

  // Selection and bulk delete state
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);

  // Single user delete state
  const [isDeletingUser, setIsDeletingUser] = useState<boolean>(false);
  const [deletingUserName, setDeletingUserName] = useState<string>('');

  // Bulk license assignment state
  const [bulkLicenseDialogOpen, setBulkLicenseDialogOpen] = useState<boolean>(false);

  // Password reset dialog state
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState<boolean>(false);
  const [userToReset, setUserToReset] = useState<TenantUser | null>(null);

  // CSV bulk import state
  const [bulkImportModalOpen, setBulkImportModalOpen] = useState<boolean>(false);
  const [csvData, setCSVData] = useState<ParsedCSVData | null>(null);
  const [csvPreviewOpen, setCSVPreviewOpen] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [csvResults, setCSVResults] = useState<BulkUserResult | null>(null);
  const [csvResultsOpen, setCSVResultsOpen] = useState<boolean>(false);
  const [csvError, setCSVError] = useState<string | null>(null);

  // Refresh counter to trigger auth methods refetch
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  // Passwordless status counts
  const [passwordlessReadyCount, setPasswordlessReadyCount] = useState<number>(0);
  const [passwordlessNotReadyCount, setPasswordlessNotReadyCount] = useState<number>(0);

  // Callback to receive passwordless counts from UserTable
  const handlePasswordlessCountsChange = useCallback((readyCount: number, notReadyCount: number) => {
    setPasswordlessReadyCount(readyCount);
    setPasswordlessNotReadyCount(notReadyCount);
  }, []);

  // Notify parent when selection changes (for embedded mode)
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedUsers.size);
    }
  }, [selectedUsers.size, onSelectionChange]);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss mutation error after 5 seconds
  useEffect(() => {
    if (mutationError) {
      const timer = setTimeout(() => setMutationError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [mutationError]);

  // Modal handlers
  const handleAddUser = () => {
    setModalMode('add');
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };

  // Expose methods to parent via ref for embedded mode
  useImperativeHandle(ref, () => ({
    refresh: () => {
      setRefreshCounter(prev => prev + 1);
      syncUsers();
    },
    openBulkImport: () => {
      setBulkImportModalOpen(true);
    },
    openAddUser: () => {
      handleAddUser();
    },
    openBulkLicense: () => {
      setBulkLicenseDialogOpen(true);
    },
    openBulkDelete: () => {
      setBulkDeleteDialogOpen(true);
    },
  }), [syncUsers]);

  const handleEditUser = (user: TenantUser) => {
    setModalMode('edit');
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleSaveUser = async (userData: Partial<Record<string, unknown>>) => {
    let createdUser: TenantUser | null = null;

    // Check if this is a role-only change (doesn't require tenantId)
    const isRoleOnlyChange = modalMode === 'edit' &&
      Object.keys(userData).length === 1 &&
      userData.role !== undefined;

    if (modalMode === 'add') {
      if (!tenantId) {
        throw new Error('Tenant ID required to create users');
      }
      // Create new user and capture response, passing organizationId if available
      createdUser = await UserApiService.createUser(tenantId, userData as unknown as UserCreateRequest, organizationId);
    } else {
      // Update existing user
      if (selectedUser) {
        // Handle role change if included in userData
        if (userData.role && typeof userData.role === 'string' && selectedUser.local_user_id) {
          await updateUserRole(selectedUser.local_user_id, userData.role as UserRole);
        }

        // Only call Graph API update if there are non-role changes
        if (!isRoleOnlyChange) {
          // Remove role from userData before sending to Graph API (role is handled separately)
          const { role: _role, ...graphUserData } = userData;
          if (Object.keys(graphUserData).length > 0) {
            // Pass tenantId and/or organizationId - backend resolves tenant from either
            await UserApiService.updateUser(
              selectedUser.id,
              graphUserData as unknown as UserUpdateRequest,
              { tenantId, organizationId }
            );
          }
        }
      }
    }

    // Refresh user list after save
    await refetchUsers();

    // Show success message for user creation
    if (createdUser) {
      // Check if communication status is present
      if (createdUser.communication_status) {
        const status = createdUser.communication_status;
        const errors = status.errors || [];

        if (errors.length > 0) {
          // Show error message for communication failures
          setMutationError(`User created successfully, but credential delivery failed: ${errors.join(', ')}`);
          setSuccessMessage(null);
        } else {
          // Show success message with communication details
          const messages: string[] = [];
          if (status.email_sent) {
            messages.push('Email sent successfully');
          }
          if (status.sms_sent) {
            messages.push('SMS sent successfully');
          }
          if (messages.length > 0) {
            setSuccessMessage(`User created successfully. ${messages.join(', ')}`);
          } else {
            setSuccessMessage('User created successfully');
          }
          setMutationError(null);
        }
      } else {
        // No communication status - show basic success message
        setSuccessMessage('User created successfully');
        setMutationError(null);
      }
    }
  };

  const handleDeleteUser = async (user: TenantUser) => {
    if (!tenantId) {
      throw new Error('Tenant ID required to delete users');
    }
    if (confirm(`Are you sure you want to delete user "${user.display_name}"?`)) {
      setIsDeletingUser(true);
      setDeletingUserName(user.display_name);
      setMutationError(null);
      setSuccessMessage(null);

      try {
        await UserApiService.deleteUser(tenantId, user.id);

        // Show success message
        setSuccessMessage(`User "${user.display_name}" deleted successfully`);

        // Refresh user list after delete
        refetchUsers();
      } catch (error) {
        // Show error message
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
        setMutationError(errorMessage);
      } finally {
        setIsDeletingUser(false);
        setDeletingUserName('');
      }
    }
  };

  const handleResetPassword = (user: TenantUser) => {
    setUserToReset(user);
    setPasswordResetDialogOpen(true);
  };

  const handlePasswordResetClose = async () => {
    setPasswordResetDialogOpen(false);
    setUserToReset(null);

    // Refresh user list after password reset
    refetchUsers();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(undefined);
  };

  // Handler for saving tenant access changes
  const handleSaveTenantAccess = async (orgId: number, userId: number, tenantIds: string[]) => {
    try {
      await OrganizationApiService.updateUserTenantAccess(orgId, userId, tenantIds);
      setSuccessMessage('Tenant access updated successfully');
      // Refresh user list to reflect changes
      refetchUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tenant access';
      setMutationError(errorMessage);
      throw err; // Re-throw so dialog knows there was an error
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(user => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Bulk delete handlers
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!tenantId) {
      throw new Error('Tenant ID required to delete users');
    }
    setIsBulkDeleting(true);
    const selectedUserIds = Array.from(selectedUsers);

    for (const userId of selectedUserIds) {
      try {
        await UserApiService.deleteUser(tenantId, userId);
      } catch {
        // Silently continue with remaining users
      }
    }

    // Close dialog and reset states after completion
    setBulkDeleteDialogOpen(false);
    setIsBulkDeleting(false);
    setSelectedUsers(new Set());

    // Refresh user list
    refetchUsers();
  };

  // Bulk license handlers
  const handleBulkLicense = () => {
    setBulkLicenseDialogOpen(true);
  };

  const handleBulkLicenseClose = async () => {
    setBulkLicenseDialogOpen(false);
    setSelectedUsers(new Set());

    // Refresh user list after bulk license operation
    refetchUsers();
  };

  // CSV import handlers
  const handleCSVParsed = (data: ParsedCSVData) => {
    setCSVData(data);
    setCSVPreviewOpen(true);
    setCSVError(null);
  };

  const handleCSVError = (error: string | Error | unknown) => {
    // Ensure error is always converted to a string
    let errorMessage: string;
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = String((error as { message: unknown }).message);
    } else {
      errorMessage = 'An unknown error occurred';
    }
    setCSVError(errorMessage);
  };

  const handleCSVConfirm = async (userRequests: UserCreateRequest[]) => {
    if (!tenantId) {
      throw new Error('Tenant ID required to create users');
    }
    setCSVPreviewOpen(false);
    setCsvLoading(true);

    try {
      const result = await UserApiService.bulkCreateUsers(tenantId, userRequests);
      setCSVResults(result);
      setCSVResultsOpen(true);
      setCSVError(null);

      // Refresh user list if any succeeded
      if (result.successful > 0) {
        refetchUsers();
      }
    } catch (err) {
      // Use handleCSVError to properly extract error message from any error type
      handleCSVError(err);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleCSVPreviewClose = () => {
    setCSVPreviewOpen(false);
    setCSVData(null);
  };

  const handleCSVResultsClose = () => {
    setCSVResultsOpen(false);
    setCSVResults(null);
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!users) {
      return [];
    }
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(user =>
      user.display_name.toLowerCase().includes(query) ||
      user.user_principal_name.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Calculate pagination based on filtered results (not total backend count)
  // This ensures pagination accurately reflects visible data when search is active
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  // Toolbar component - shared between embedded and non-embedded modes
  const renderToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => {
            setRefreshCounter(prev => prev + 1);
            syncUsers();
          }}
          data-testid="refresh-users-button"
          loading={isSyncing}
          loadingText="Refreshing..."
        >
          Refresh
        </ToolbarButton>
        {!embedded && <TenantContextSelector />}
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        {tenantId && (
          <ToolbarSearch
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            data-testid="user-search"
          />
        )}
        {(users?.length ?? 0) > 0 && (
          <>
            <ToolbarButton
              icon={<Certificate20Regular />}
              data-testid="bulk-license-button"
              onClick={handleBulkLicense}
              disabled={selectedUsers.size === 0}
            >
              {selectedUsers.size > 0 ? `Licenses (${selectedUsers.size})` : 'Licenses'}
            </ToolbarButton>
            <ToolbarButton
              icon={<Delete20Regular />}
              data-testid="bulk-delete-button"
              onClick={handleBulkDelete}
              disabled={selectedUsers.size === 0}
            >
              {selectedUsers.size > 0 ? `Delete (${selectedUsers.size})` : 'Delete'}
            </ToolbarButton>
          </>
        )}
        <ToolbarButton
          icon={<ArrowUpload20Regular />}
          onClick={() => setBulkImportModalOpen(true)}
          disabled={!tenantId}
          data-testid="bulk-import-button"
        >
          Bulk Import
        </ToolbarButton>
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={handleAddUser}
          disabled={!tenantId}
          data-testid="add-user-button"
        >
          Add User
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  // Content component - shared between embedded and non-embedded modes
  const renderContent = () => (
    <>
      {/* Alert messages */}
      {!tenantId && !embedded && (
        <Alert intent="info">
          <Text>Please select a tenant from the dropdown to manage users.</Text>
        </Alert>
      )}
      {csvError && (
        <Alert intent="error">
          <Text>{csvError}</Text>
        </Alert>
      )}
      {mutationError && (
        <Alert intent="error">
          <Text>{mutationError}</Text>
        </Alert>
      )}
      {successMessage && (
        <Alert intent="success">
          <Text>{successMessage}</Text>
        </Alert>
      )}
      {isDeletingUser && (
        <Alert intent="info" data-testid="delete-user-loading">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Spinner size="tiny" />
            <Text>Deleting user &ldquo;{deletingUserName}&rdquo;...</Text>
          </div>
        </Alert>
      )}

      {/* Toolbar - always show so tenant selector is accessible */}
      {renderToolbar()}

      {/* User list in card with pagination footer */}
      {(tenantId || organizationId) && (
        <RoundedCard style={{ padding: 0, gap: 0 }}>
          <UserList
            tenantId={tenantId ?? currentTenant?.tenant_id ?? ''}
            loading={loading}
            users={filteredUsers}
            error={error}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onResetPassword={handleResetPassword}
            selectedUsers={selectedUsers}
            onSelectAll={handleSelectAll}
            onSelectUser={handleSelectUser}
            currentUserIsSuperadmin={isGlobalAdmin(currentUser)}
            columnView={columnViewProp ?? (organizationId && embedded ? 'organization' : (selectedTab === 'auth' ? 'auth-only' : 'full'))}
            refreshTrigger={refreshCounter}
            onPasswordlessCountsChange={handlePasswordlessCountsChange}
            organizationTenants={organizationTenants}
            organizationId={organizationId}
            onSaveTenantAccess={handleSaveTenantAccess}
          />
          {/* Pagination footer */}
          {!loading && !error && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Showing {filteredUsers.length} of {totalUsers} users
              </span>
              <div className={styles.paginationControls}>
                <Button
                  appearance="subtle"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  data-testid="prev-page"
                >
                  Previous
                </Button>
                <span className={styles.pageText}>
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  appearance="subtle"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  data-testid="next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </RoundedCard>
      )}
    </>
  );

  // Render based on embedded mode
  if (embedded) {
    return (
      <div data-testid="user-management-page">
        {renderContent()}
        {renderModals()}
      </div>
    );
  }

  return (
    <PageLayout
      title="User Management"
      tabs={[
        { value: 'users', label: 'Users' },
        { value: 'auth', label: 'Authentication Status' },
      ]}
      selectedTab={selectedTab}
      onTabChange={(value) => {
        setSelectedTab(value as 'users' | 'auth');
        localStorage.setItem('userManagementSelectedTab', value);
      }}
      headerActions={
        selectedTab === 'auth' && (users?.length ?? 0) > 0 ? (
          <PasswordlessStatusIndicator
            readyCount={passwordlessReadyCount}
            notReadyCount={passwordlessNotReadyCount}
          />
        ) : undefined
      }
      testId="user-management-page"
    >
      {renderContent()}
      {renderModals()}
    </PageLayout>
  );

  // Helper function to render all modals
  function renderModals() {
    return (
      <>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        mode={modalMode}
        tenantId={tenantId ?? ''}
        existingUser={selectedUser}
        organizationTenants={organizationTenants}
        organizationId={organizationId}
        onSaveTenantAccess={handleSaveTenantAccess}
        availableOrganizations={availableOrganizations}
        isHomeTenant={currentTenant?.tenant_id === currentUser?.tenant_id}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(_, data) => {
        if (!data.open) {
          setBulkDeleteDialogOpen(false);
        }
      }}>
        <DialogSurface data-testid="bulk-delete-dialog" style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''}? This action cannot be undone.
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Text weight="semibold">Users to be deleted:</Text>
                <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                  {Array.from(selectedUsers).map(userId => {
                    const user = users?.find(u => u.id === userId);
                    if (user) {
                      return (
                        <li key={userId} style={{ marginBottom: '4px' }}>
                          {user.display_name}
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                data-testid="bulk-delete-cancel-button"
                onClick={() => setBulkDeleteDialogOpen(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                data-testid="bulk-delete-confirm-button"
                onClick={handleConfirmBulkDelete}
                disabled={isBulkDeleting}
                icon={isBulkDeleting ? <Spinner size="tiny" /> : undefined}
              >
                {isBulkDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* CSV Import Modals */}
      <BulkImportModal
        isOpen={bulkImportModalOpen}
        onClose={() => setBulkImportModalOpen(false)}
        onCSVParsed={handleCSVParsed}
        onError={handleCSVError}
        disabled={loading || csvLoading}
      />
      <CSVPreviewModal
        isOpen={csvPreviewOpen}
        csvData={csvData}
        onClose={handleCSVPreviewClose}
        onConfirm={handleCSVConfirm}
      />
      <CSVResultsModal
        isOpen={csvResultsOpen}
        result={csvResults}
        onClose={handleCSVResultsClose}
      />

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        isOpen={passwordResetDialogOpen}
        onClose={handlePasswordResetClose}
        user={userToReset}
        tenantId={tenantId ?? ''}
      />

      {/* Bulk License Dialog */}
      <BulkLicenseDialog
        isOpen={bulkLicenseDialogOpen}
        onClose={handleBulkLicenseClose}
        tenantId={tenantId ?? ''}
        selectedUsers={(users ?? []).filter(user => selectedUsers.has(user.id))}
      />
      </>
    );
  }
});
